/* bosi-zoom-instant-v2 */
import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';
import { scrollIntoView } from '@theme/scrolling';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';
/**
 * A custom element that renders a zoom dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement[]} media - The media elements.
 * @property {HTMLElement} thumbnails - The thumbnails elements.
 *
 * @extends Component<Refs>
 */
export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'media', 'thumbnails'];

  #highResImagesLoaded = /** @type {Set<string>} */ (new Set());
  #highResImagesLoading = new Set();
  #loopBefore = null;
  #loopAfter = null;
  #activeIndex = -1;
  #scrollFrame = 0;
  #settleTimer = 0;
  #touching = false;

  get #gallery() {
    return this.refs.dialog.querySelector('.dialog-zoomed-gallery');
  }

  get #mobile() {
    return window.matchMedia('(max-width: 749px)').matches;
  }

  connectedCallback() {
    super.connectedCallback();
    this.refs.dialog.addEventListener('scroll', this.handleScroll);
    this.refs.dialog.addEventListener('gallery:zoom', this.handleZoom);
    this.refs.dialog.querySelector('.dialog-zoomed-gallery')?.addEventListener('scroll', this.handleScroll);
    this.#gallery?.addEventListener('scrollend', this.finishScroll);
    this.#gallery?.addEventListener('touchstart', this.handleGalleryTouchStart, { passive: true });
    this.#gallery?.addEventListener('touchend', this.handleGalleryTouchEnd, { passive: true });
    this.#gallery?.addEventListener('touchcancel', this.handleGalleryTouchEnd, { passive: true });
    window.addEventListener('resize', this.handleResize);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.dialog.removeEventListener('scroll', this.handleScroll);
    this.refs.dialog.removeEventListener('gallery:zoom', this.handleZoom);
    this.refs.dialog.querySelector('.dialog-zoomed-gallery')?.removeEventListener('scroll', this.handleScroll);
    this.#gallery?.removeEventListener('scrollend', this.finishScroll);
    this.#gallery?.removeEventListener('touchstart', this.handleGalleryTouchStart);
    this.#gallery?.removeEventListener('touchend', this.handleGalleryTouchEnd);
    this.#gallery?.removeEventListener('touchcancel', this.handleGalleryTouchEnd);
    window.removeEventListener('resize', this.handleResize);
    cancelAnimationFrame(this.#scrollFrame);
    clearTimeout(this.#settleTimer);
  }

  /**
   * Opens the zoom dialog.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  async open(index, event) {
    event?.preventDefault?.();

    const { dialog } = this.refs;

    // Instant open — no View Transition morph. Packshot geometry in the
    // lightbox ≠ gallery crop, so morphing looked like a laggy slide/shift.
    dialog.style.backgroundColor = '#ffffff';
    dialog.showModal();

    this.setupLoop();
    this.#activeIndex = -1;
    this.selectThumbnail(index, { behavior: 'instant' });
  }

  /**
   * Loads a high-resolution image for a specific media container
   * @param {HTMLElement} mediaContainer - The media container element
   */
  loadHighResolutionImage(mediaContainer) {
    if (!mediaContainer.classList.contains('product-media-container--image')) return false;
    if (window.matchMedia('(max-width: 749px)').matches && !mediaContainer.querySelector('drag-zoom-wrapper')?.isZoomed) return false;

    const image = mediaContainer.querySelector('img.product-media__image');
    if (!image || !(image instanceof HTMLImageElement)) return false;

    const highResolutionUrl = image.getAttribute('data_max_resolution');
    if (!highResolutionUrl || this.#highResImagesLoaded.has(highResolutionUrl) || this.#highResImagesLoading.has(highResolutionUrl)) return false;
    this.#highResImagesLoading.add(highResolutionUrl);

    const newImage = new Image();
    newImage.className = image.className;
    newImage.alt = image.alt;
    newImage.setAttribute('data_max_resolution', highResolutionUrl);
    newImage.setAttribute('ref', 'image');

    // When the high-resolution image loads, replace the existing image
    newImage.onload = () => {
      image.replaceWith(newImage);
      this.#highResImagesLoading.delete(highResolutionUrl);
      this.#highResImagesLoaded.add(highResolutionUrl);
    };
    newImage.onerror = () => this.#highResImagesLoading.delete(highResolutionUrl);

    newImage.src = highResolutionUrl;
  }

  /**
   * Handles the scroll event of the dialog, which is used to update the active thumbnail when the corresponding image is visible in the main view.
   * @param {Event} event - The scroll event.
   */
  handleScroll = () => {
    if (!this.refs.dialog.open) return;
    if (!this.#scrollFrame) this.#scrollFrame = requestAnimationFrame(() => {
      this.#scrollFrame = 0;
      this.syncSelection();
    });
    clearTimeout(this.#settleTimer);
    this.#settleTimer = window.setTimeout(this.finishScroll, 120);
  };

  syncSelection() {
    if (!this.refs.dialog.open) return;
    const { media, dialog } = this.refs;
    const gallery = this.#gallery;
    if (this.#mobile && gallery?.clientWidth) {
      const slot = Math.round(gallery.scrollLeft / gallery.clientWidth);
      const index = this.#loopBefore
        ? (slot - 1 + media.length) % media.length
        : Math.max(0, Math.min(media.length - 1, slot));
      this.setActive(index);
      return;
    }
    const bounds = dialog.getBoundingClientRect();
    let bestIndex = this.#activeIndex;
    let bestArea = 0;
    media.forEach((element, index) => {
      const rect = element.getBoundingClientRect();
      const width = Math.max(0, Math.min(rect.right, bounds.right) - Math.max(rect.left, bounds.left));
      const height = Math.max(0, Math.min(rect.bottom, bounds.bottom) - Math.max(rect.top, bounds.top));
      const area = width * height;
      if (area > bestArea) {
        bestArea = area;
        bestIndex = index;
      }
    });
    this.setActive(bestIndex);
  }

  setActive(index) {
    const { media, thumbnails } = this.refs;
    if (index < 0 || index >= media.length || index === this.#activeIndex) return;
    this.#activeIndex = index;
    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      button.setAttribute('aria-selected', `${i === index}`);
    });
    const thumbnail = thumbnails.children[index];
    if (thumbnail) scrollIntoView(thumbnail, { ancestor: thumbnails, behavior: 'instant', block: 'nearest', inline: 'nearest' });
    this.updateStatus(index);
    this.dispatchEvent(new ZoomMediaSelectedEvent(index));
    this.warmImages(index);
    this.loadHighResolutionImage(media[index]);
  }

  setupLoop() {
    const { media } = this.refs;
    const gallery = this.#gallery;
    if (!gallery) return;
    if (!this.#mobile || media.length < 2) {
      this.#loopBefore?.remove();
      this.#loopAfter?.remove();
      this.#loopBefore = null;
      this.#loopAfter = null;
      return;
    }
    if (this.#loopBefore?.isConnected && this.#loopAfter?.isConnected) return;
    this.#loopBefore = this.createLoopSlide(media[media.length - 1]);
    this.#loopAfter = this.createLoopSlide(media[0]);
    gallery.prepend(this.#loopBefore);
    gallery.append(this.#loopAfter);
  }

  createLoopSlide(source) {
    const slide = source.cloneNode(false);
    Array.from(slide.attributes).forEach((attribute) => {
      if (attribute.name === 'id' || attribute.name === 'ref' || attribute.name.startsWith('on:')) slide.removeAttribute(attribute.name);
    });
    slide.classList.add('bosi-zoom-loop-slide');
    slide.setAttribute('aria-hidden', 'true');
    slide.inert = true;
    const image = source.querySelector('img');
    if (image) {
      const wrapper = document.createElement('div');
      wrapper.className = 'product-media__drag-zoom-wrapper';
      const content = source.querySelector('.product-media')?.cloneNode(false) || document.createElement('div');
      content.classList.add('product-media');
      content.removeAttribute('id');
      content.removeAttribute('ref');
      const copy = image.cloneNode(true);
      copy.removeAttribute('ref');
      copy.removeAttribute('id');
      copy.alt = '';
      copy.loading = 'eager';
      copy.decoding = 'async';
      content.append(copy);
      wrapper.append(content);
      slide.append(wrapper);
    }
    return slide;
  }

  positionMedia(index, behavior) {
    const gallery = this.#gallery;
    if (this.#mobile && gallery?.clientWidth) {
      gallery.scrollTo({ left: (index + (this.#loopBefore ? 1 : 0)) * gallery.clientWidth, behavior });
    } else {
      this.refs.media[index]?.scrollIntoView({ behavior, block: 'nearest', inline: 'start' });
    }
  }

  normalizeLoop() {
    const gallery = this.#gallery;
    const length = this.refs.media.length;
    if (!this.#loopBefore || !gallery?.clientWidth || this.#touching || !this.refs.dialog.open) return;
    if (Math.abs(gallery.scrollLeft) < 2) this.positionMedia(length - 1, 'instant');
    else if (Math.abs(gallery.scrollLeft - (length + 1) * gallery.clientWidth) < 2) this.positionMedia(0, 'instant');
  }

  finishScroll = () => {
    clearTimeout(this.#settleTimer);
    this.syncSelection();
    this.normalizeLoop();
  };

  handleGalleryTouchStart = () => {
    this.normalizeLoop();
    this.#touching = true;
  };

  handleGalleryTouchEnd = () => {
    this.#touching = false;
    clearTimeout(this.#settleTimer);
    this.#settleTimer = window.setTimeout(this.finishScroll, 120);
  };

  handleResize = () => {
    if (!this.refs.dialog.open) return;
    const index = Math.max(0, this.#activeIndex);
    this.setupLoop();
    this.positionMedia(index, 'instant');
  };

  /**
   * Closes the zoom dialog.
   */
  async close() {
    // Instant close — skipping View Transition avoids the thumbnail wait +
    // morph shift back into the gallery frame.
    this.closeDialog();
  }

  closeDialog() {
    const { dialog } = this.refs;
    dialog.close();
    this.#touching = false;
    clearTimeout(this.#settleTimer);
    window.dispatchEvent(new DialogCloseEvent());
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown(event) {
    if (!this.refs.dialog.open) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }
    if (event.altKey || event.ctrlKey || event.metaKey) return;
    if (event.target instanceof Element && event.target.closest('input, textarea, select, [contenteditable="true"], video, audio, model-viewer')) return;
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    event.preventDefault();
    this.navigate(event.key === 'ArrowRight' ? 1 : -1);
  }

  previous() {
    this.navigate(-1);
  }

  next() {
    this.navigate(1);
  }

  handleZoom = (event) => {
    const container = event.target.closest('[ref="media[]"]');
    if (this.refs.dialog.open && container) this.loadHighResolutionImage(container);
  };

  warmImages(index) {
    const { media } = this.refs;
    for (const adjacent of [index, index - 1, index + 1]) {
      const image = media[(adjacent + media.length) % media.length]?.querySelector('img.product-media__image');
      if (image) {
        image.loading = 'eager';
        image.decoding = 'async';
      }
    }
  }

  navigate(direction) {
    const { thumbnails, media } = this.refs;
    if (!this.refs.dialog.open || media.length < 2) return;
    const buttons = Array.from(thumbnails.querySelectorAll('button'));
    const activeIndex = Math.max(0, buttons.findIndex((button) => button.getAttribute('aria-selected') === 'true'));
    const index = (activeIndex + direction + media.length) % media.length;
    if (this.#loopBefore && (activeIndex + direction < 0 || activeIndex + direction >= media.length)) {
      this.setActive(index);
      this.#gallery.scrollTo({ left: direction > 0 ? (media.length + 1) * this.#gallery.clientWidth : 0, behavior: prefersReducedMotion() ? 'instant' : 'smooth' });
      return;
    }
    this.selectThumbnail(index, { behavior: prefersReducedMotion() ? 'instant' : 'smooth' });
  }

  updateStatus(index) {
    const status = this.querySelector('[ref="status"]');
    if (status) status.textContent = (this.dataset.slideStatus || '').replace('[index]', String(index + 1));
  }

  /**
   * Handles the click event of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   */
  async handleThumbnailClick(index) {
    const behavior = prefersReducedMotion() ? 'instant' : 'smooth';
    this.selectThumbnail(index, { behavior });
  }

  /**
   * Handles the pointer enter event of a thumbnail.
   * @param {number} index - The index of the thumbnail to load the high-resolution image for.
   */
  async handleThumbnailPointerEnter(index) {
    const { media } = this.refs;
    if (!media[index]) return;

    this.loadHighResolutionImage(media[index]);
  }

  /**
   * Handles the selection of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   * @param {Object} options - The options for the selection.
   * @param {ScrollBehavior} options.behavior - The behavior of the scroll.
   */
  async selectThumbnail(index, options = { behavior: 'smooth' }) {
    if (!this.refs.thumbnails || !this.refs.thumbnails.children.length) return;

    // Guard if invalid
    if (isNaN(index) || index < 0 || index >= this.refs.thumbnails.children.length) return;

    const { media, thumbnails } = this.refs;
    const targetThumbnail = thumbnails.children[index];

    if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;

    if (targetThumbnail.getAttribute('aria-selected') !== 'true') {
      this.querySelectorAll('drag-zoom-wrapper').forEach((wrapper) => wrapper.resetZoom?.());
      this.querySelectorAll('video, audio').forEach((player) => player.pause());
    }

    this.setActive(index);

    scrollIntoView(targetThumbnail, {
      ancestor: thumbnails,
      behavior: options.behavior,
      block: 'center',
      inline: 'center',
    });

    this.positionMedia(index, options.behavior);
  }
}

if (!customElements.get('zoom-dialog')) {
  customElements.define('zoom-dialog', ZoomDialog);
}

